import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

const ENGINE_CARDS = 8;
/** Pause in typing before wine cards are requested (ms), the rhythm of the standard search: no request per keystroke. */
const ENGINE_CARDS_DELAY = 200;
const ENGINE_LINES_PER_GROUP = 3;
/** Pause in typing before the server directory is asked about a term the cellar does not hold (ms). */
const DIRECTORY_DELAY = 200;

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** @type {import('@theme/search-client').SearchClient | null} */
  #engineClient = null;
  #engineBroken = false;
  /** @type {() => Promise<void>} */
  #enginePreload = () => Promise.resolve();
  #engineTerm = '';
  #engineCardsKey = '';
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #directoryTimer;
  /** @type {any} */
  #journal = null;
  #engineShownAt = 0;
  #engineCardsTimer = 0;
  #engineTexts = null;

  get #engineOn() {
    return this.dataset.engine === 'engine' && !this.#engineBroken;
  }

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });

    if (this.#engineOn) this.#setupEngine(signal);
  }

  /** @param {AbortSignal} signal */
  #setupEngine(signal) {
    // ?search_internal=1 marks this browser as internal for search measurement, ?search_internal=0 removes the mark
    try {
      const flag = new URLSearchParams(location.search).get('search_internal');
      if (flag === '1') localStorage.setItem('search_internal', '1');
      if (flag === '0') localStorage.removeItem('search_internal');
    } catch (error) {
      // storage unavailable: measurement stays on
    }
    const { cardsSection } = this.dataset;
    if (!this.dataset.indexUrl) return;
    /** @type {Promise<void> | null} */
    let loading = null;
    let loadingUrl = '';
    const preload = () => {
      const indexUrl = this.dataset.indexUrl || '';
      // A section re-render can change the visitor's country without a page load: the index and the cards
      // kept so far belong to the previous country (catalogue, prices), so the client is replaced.
      if (loadingUrl !== indexUrl) {
        if (this.#engineClient?.url !== indexUrl) this.#engineClient = null;
        loading = null;
        loadingUrl = indexUrl;
        delete this.dataset.engineReady;
      }
      if (!indexUrl) return Promise.resolve();
      loading ??= Promise.all([import('@theme/search-client'), import('@theme/search-journal').catch(() => null)])
        .then(([{ SearchClient }, journal]) => {
          if (loadingUrl !== indexUrl) return; // country changed meanwhile, a newer load takes over
          this.#engineClient ??= new SearchClient({ indexUrl, cardsSection: cardsSection || 'search-cards', searchUrl: Theme.routes.search_url, directoryUrl: this.dataset.directoryUrl });
          const { logUrl, country, currency } = this.dataset;
          this.#journal = journal ? new journal.SearchJournal({ endpoint: logUrl, surface: 'instant', country, currency, indexUrl }) : null;
          return this.#engineClient.preload();
        })
        .then(() => {
          if (this.#engineClient?.ready && this.#engineClient.url === this.dataset.indexUrl) this.dataset.engineReady = '1';
        })
        .catch(() => {});
      return loading;
    };
    this.#enginePreload = preload;
    for (const button of document.querySelectorAll('.search-modal__button')) {
      for (const type of ['pointerenter', 'focus', 'touchstart']) button.addEventListener(type, preload, { signal, passive: true, once: true });
    }
    this.refs.searchInput.addEventListener('focus', preload, { signal, once: true });
    this.refs.searchInput.addEventListener('input', this.#onEngineInput, { signal });
    this.addEventListener('click', this.#onEngineClick, { signal, capture: true });
  }

  /** @param {MouseEvent} event */
  #onEngineClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('[data-search-results] a');
    if (!link || !this.#journal) return;
    const q = this.refs.searchInput.value.trim();
    const card = link.closest('li[data-product-id]');
    if (card instanceof HTMLElement) {
      const cards = [...this.querySelectorAll('[data-search-cards-target] li[data-product-id]')];
      this.#journal.click({ q, id: Number(card.dataset.productId), pos: cards.indexOf(card) + 1, group: 'card' });
    } else {
      const lines = [...this.querySelectorAll('[data-search-lines] a')];
      this.#journal.click({ q, id: null, pos: lines.indexOf(link) + 1, group: 'line', name: link.textContent || '' });
    }
  };

  /** @param {Event} event */
  #onEngineInput = (event) => {
    if (!this.#engineOn || !(event instanceof InputEvent) || !event.inputType) return;
    const client = this.#engineClient;
    if (!client?.ready || client.url !== this.dataset.indexUrl) {
      this.#enginePreload();
      return;
    }
    const term = this.refs.searchInput.value.trim();
    this.#engineTerm = term;
    if (this.#activeFetch) {
      this.#activeFetch.abort();
      this.#activeFetch = null;
    }
    try {
      this.#engineSearch(term);
    } catch (error) {
      this.#engineBroken = true;
      this.#engineTerm = '';
    }
  };

  /** @param {string} term */
  #engineSearch(term) {
    const client = /** @type {SearchClient} */ (this.#engineClient);
    this.#currentIndex = -1;
    clearTimeout(this.#directoryTimer);
    if (!term.length) {
      this.#journal?.cancel();
      this.#engineCardsKey = '';
      this.#resetSearch();
      return;
    }
    this.#showResetButton();
    const result = client.search(term, { instant: true });
    if (!result || result.state === 'empty') {
      this.#engineCardsKey = '';
      this.#renderEngine({ lines: [], note: '', cards: [] });
      return;
    }
    this.#engineShow(term, result);
    if (result.state === 'unknown' || result.corrected.length) this.#askDirectory(term, result.state === 'unknown');
  }

  /**
   * When the cellar has nothing for the term, or only after correcting a word, the server directory may know the
   * producer or place. Asked once typing pauses, after the local answer is shown; after a correction, only a match
   * on the whole name replaces the local answer. No answer, a refusal or a failure leaves the local answer.
   * @param {string} term
   * @param {boolean} unknown
   */
  #askDirectory(term, unknown) {
    this.#directoryTimer = setTimeout(async () => {
      const client = this.#engineClient;
      if (!client?.ready || this.#engineTerm !== term) return;
      const hit = await client.directory(term);
      if (!hit || this.#engineTerm !== term || client !== this.#engineClient || (!unknown && !hit.exact)) return;
      const result = client.absent(hit);
      if (result) this.#engineShow(term, result);
    }, DIRECTORY_DELAY);
  }

  /**
   * Lines, note and cards for one engine answer.
   * @param {string} term
   * @param {any} result
   */
  #engineShow(term, result) {
    const client = /** @type {SearchClient} */ (this.#engineClient);
    const texts = this.#texts();
    const fill = (text, values) => Object.entries(values).reduce((t, [k, v]) => t.split('%' + k + '%').join(v), text);
    let note = '';
    let ids = result.ids.slice(0, ENGINE_CARDS).map((idx) => client.prods[idx].i);
    let total = result.ids.length;
    const producerName = result.ents.find((e) => e.type === 'P')?.name || term;
    let lineEnts = result.ents;
    if (result.state === 'known_absent' && result.absent) {
      note = fill(texts.not_in_cellar, { name: result.absent.name });
      const nearby = result.absent.region ? client.search(result.absent.region, { instant: false }) : null;
      ids = nearby && nearby.ids.length ? nearby.ids.slice(0, ENGINE_CARDS).map((idx) => client.prods[idx].i) : [];
      total = nearby ? nearby.ids.length : 0;
      if (ids.length) note += ' ' + fill(texts.nearby, { region: result.absent.region });
      if (nearby) lineEnts = nearby.ents;
    } else if (result.state === 'unknown') {
      note = fill(texts.nothing_found, { terms: term });
      ids = [];
      total = 0;
    } else if (result.state === 'relaxed' && result.year && result.relaxed.includes('y')) {
      note = fill(texts.no_vintage, { year: String(result.year), name: producerName });
    } else if (result.state === 'relaxed' && result.relaxed.includes('F')) {
      const format = result.ents.find((e) => e.type === 'F')?.name || '';
      const place = result.ents.find((e) => e.type === 'P' || e.type === 'A' || e.type === 'R')?.name || term;
      note = fill(texts.no_format, { format, name: place });
    } else if (result.state === 'producer_without_wine') {
      note = fill(texts.producer_without_wine, { name: producerName });
    }
    const seen = new Set();
    const pick = (types) => lineEnts.filter((e) => types.includes(e.type) && !seen.has(e.name) && seen.add(e.name)).slice(0, ENGINE_LINES_PER_GROUP);
    const producers = pick('P');
    const collections = pick('L');
    const appellations = pick('ASRC');
    const vintageLines = [];
    if (result.year && result.state === 'found' && result.ids.some((idx) => client.prods[idx].y === result.year)) {
      const vid = client.vintageId(result.year);
      if (vid && texts.see_all_vintage && this.dataset.vintageUrl) {
        vintageLines.push({ name: fill(texts.see_all_vintage, { year: String(result.year) }), url: `${this.dataset.vintageUrl}?filter.p.m.wine_info.vintage=${encodeURIComponent('gid://shopify/Metaobject/' + vid)}` });
      }
    }
    const groups = [
      ['producers', texts.producers, producers],
      ['collections', texts.collections, collections],
      ['appellations', texts.appellations, appellations],
      ['vintage', '', vintageLines],
    ];
    this.#renderEngine({ lines: groups, note, cards: ids });
    this.#engineShownAt = performance.now();
    this.#journal?.search({ q: term, n: client.normalize(term), result, results: total, ids, top: client.describe(ids), ms: result.ms });
  }

  #texts() {
    if (!this.#engineTexts) {
      const script = this.querySelector('[data-search-texts]');
      const decode = (s) => {
        const el = document.createElement('textarea');
        el.innerHTML = String(s);
        return el.value;
      };
      this.#engineTexts = script ? Object.fromEntries(Object.entries(JSON.parse(script.textContent || '{}')).map(([k, v]) => [k, decode(v)])) : {};
    }
    return this.#engineTexts;
  }

  /**
   * @param {{ lines: Array<[string, string, Array<{name: string, url?: string}>]>, note: string, cards: number[] }} view
   */
  #renderEngine(view) {
    const { predictiveSearchResults } = this.refs;
    const escape = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    const searchUrl = Theme.routes.search_url;
    let lines = '';
    for (const [group, title, ents] of view.lines) {
      if (!ents.length) continue;
      if (title) lines += `<div class="predictive-search-results__resource-header"><h4 class="predictive-search-results__title">${escape(title)}</h4></div>`;
      lines += `<ul class="predictive-search-results__collection-pills predictive-search-results__wrapper-queries list-unstyled" role="listbox" data-group="${group}">`;
      for (const e of ents) {
        const href = e.url || `${searchUrl}?q=${encodeURIComponent(e.name)}`;
        lines += `<li class="predictive-search-results__card--query" ref="resultsItems[]"><a class="pills__pill predictive-search-results__pill" href="${escape(href)}">${escape(e.name)}</a></li>`;
      }
      lines += '</ul>';
    }
    const previousCards = predictiveSearchResults.querySelector('[data-search-cards-target]');
    const keepCards = view.cards.length && previousCards ? previousCards.innerHTML : '';
    predictiveSearchResults.innerHTML =
      `<div id="predictive-search-results" class="predictive-search-dropdown" role="listbox" aria-expanded="true" style="--color-shadow: rgb(var(--color-foreground-rgb) / var(--opacity-10-25));">` +
      `<div class="predictive-search-results__inner" data-search-results>` +
      (lines ? `<div class="predictive-search-results__collections" data-search-lines>${lines}</div>` : '<div data-search-lines hidden></div>') +
      (view.note ? `<p class="predictive-search-results__note" data-search-note>${escape(view.note)}</p>` : '') +
      `<div class="predictive-search-results__products" data-search-cards-target>${keepCards}</div>` +
      `</div></div>`;
    this.#resetScrollPositions();
    this.#scheduleEngineCards(view.cards);
  }

  /** @param {number[]} ids */
  #scheduleEngineCards(ids) {
    const key = ids.join(',');
    this.#pendingEngineIds = key;
    clearTimeout(this.#engineCardsTimer);
    if (key === this.#engineCardsKey) return;
    if (!ids.length) {
      this.#engineCardsKey = '';
      return;
    }
    this.#engineCardsTimer = setTimeout(async () => {
      const client = this.#engineClient;
      if (!client) return;
      try {
        const cards = await client.cards(ids);
        if (ids.join(',') !== this.#lastEngineIds()) return;
        const target = this.refs.predictiveSearchResults.querySelector('[data-search-cards-target]');
        if (!target) return;
        const html = ids.map((id) => cards.get(id) || '').join('');
        const title = this.#texts().wines || '';
        target.innerHTML = html ? `<h4 class="predictive-search-results__title">${title.replace(/[&<>"]/g, '')}</h4><ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled" role="listbox">${html}</ul>` : '';
        this.#engineCardsKey = key;
        this.#journal?.cardsShown(performance.now() - this.#engineShownAt);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        const term = this.refs.searchInput.value.trim();
        if (!term.length || ids.join(',') !== this.#lastEngineIds()) return;
        this.#engineTerm = '';
        this.#engineCardsKey = '';
        this.#getSearchResults(term);
      }
    }, ENGINE_CARDS_DELAY);
  }

  #pendingEngineIds = '';
  #lastEngineIds() {
    return this.#pendingEngineIds;
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#journal?.flush();
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          this.#journal?.flush();
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    if (this.#engineOn && this.#engineClient?.ready && this.#engineTerm === searchTerm) return;
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
